import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, User, RefreshCw, Trophy, ArrowDown } from "lucide-react";
import GameOptions from "./GameOptions";
import { useGameBot } from "./useGameBot";
import PlayerNameInput from "./PlayerNameInput";

const ConnectFourGame = ({ onBack }) => {
  const navigate = useNavigate();

  // Settings
  const [gameMode, setGameMode] = useState("PvC");
  const [difficulty, setDifficulty] = useState("Easy");
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");

  // Game Constants
  const ROWS = 6;
  const COLS = 7;
  // Board: 2D array [row][col]. 0=Empty, 1=P1, 2=P2
  // Row 0 is TOP, Row 5 is BOTTOM visually.
  const [board, setBoard] = useState(
      Array(ROWS).fill(null).map(() => Array(COLS).fill(0))
  );
  
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [winningCells, setWinningCells] = useState([]); // Array of {r, c} for highlighting

  const getPlayerName = (id) => {
    if (gameMode === "PvC" && id === 2) return "Computer";
    return id === 1 ? player1Name : player2Name;
  };

  const startGame = () => {
    setBoard(Array(ROWS).fill(null).map(() => Array(COLS).fill(0)));
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
    setWinningCells([]);
    setGameStarted(true);
  };

  // --------------------------------------------------------------------------
  // Core Logic
  // --------------------------------------------------------------------------

  // Find the first empty row in a column (checking from bottom up)
  const getLowestEmptyRow = (currentBoard, col) => {
      for (let r = ROWS - 1; r >= 0; r--) {
          if (currentBoard[r][col] === 0) {
              return r;
          }
      }
      return -1; // Column full
  };

  const checkWin = (b, player) => {
     // Check all directions
     // Horizontal
     for (let r = 0; r < ROWS; r++) {
         for (let c = 0; c <= COLS - 4; c++) {
             if (b[r][c] === player && b[r][c+1] === player && b[r][c+2] === player && b[r][c+3] === player) {
                 return [{r,c}, {r,c:c+1}, {r,c:c+2}, {r,c:c+3}];
             }
         }
     }
     // Vertical
     for (let c = 0; c < COLS; c++) {
         for (let r = 0; r <= ROWS - 4; r++) {
             if (b[r][c] === player && b[r+1][c] === player && b[r+2][c] === player && b[r+3][c] === player) {
                 return [{r,c}, {r:r+1,c}, {r:r+2,c}, {r:r+3,c}];
             }
         }
     }
     // Diagonal Down-Right
     for (let r = 0; r <= ROWS - 4; r++) {
         for (let c = 0; c <= COLS - 4; c++) {
             if (b[r][c] === player && b[r+1][c+1] === player && b[r+2][c+2] === player && b[r+3][c+3] === player) {
                 return [{r,c}, {r:r+1,c:c+1}, {r:r+2,c:c+2}, {r:r+3,c:c+3}];
             }
         }
     }
     // Diagonal Up-Right (or Down-Left relative to array indices)
     // Start from r=3 down to 5
     for (let r = 3; r < ROWS; r++) {
         for (let c = 0; c <= COLS - 4; c++) {
             if (b[r][c] === player && b[r-1][c+1] === player && b[r-2][c+2] === player && b[r-3][c+3] === player) {
                 return [{r,c}, {r:r-1,c:c+1}, {r:r-2,c:c+2}, {r:r-3,c:c+3}];
             }
         }
     }
     return null;
  };

  // --------------------------------------------------------------------------
  // AI Logic (Minimax)
  // --------------------------------------------------------------------------
  
  const evaluateWindow = (window, piece) => {
      let score = 0;
      const oppPiece = piece === 1 ? 2 : 1;
      const pieceCount = window.filter(cell => cell === piece).length;
      const emptyCount = window.filter(cell => cell === 0).length;
      const oppCount = window.filter(cell => cell === oppPiece).length;

      if (pieceCount === 4) score += 100;
      else if (pieceCount === 3 && emptyCount === 1) score += 5;
      else if (pieceCount === 2 && emptyCount === 2) score += 2;

      // Block opponent
      if (oppCount === 3 && emptyCount === 1) score -= 4;

      return score;
  };

  const scorePosition = (b, piece) => {
      let score = 0;
      
      // Center column preference
      const centerArray = [];
      const centerCol = Math.floor(COLS/2);
      for(let r=0; r<ROWS; r++) centerArray.push(b[r][centerCol]);
      const centerCount = centerArray.filter(c => c === piece).length;
      score += centerCount * 3;

      // Horizontal
      for (let r = 0; r < ROWS; r++) {
          for (let c = 0; c <= COLS - 4; c++) {
              const window = [b[r][c], b[r][c+1], b[r][c+2], b[r][c+3]];
              score += evaluateWindow(window, piece);
          }
      }
      // Vertical
      for (let c = 0; c < COLS; c++) {
          for (let r = 0; r <= ROWS - 4; r++) {
              const window = [b[r][c], b[r+1][c], b[r+2][c], b[r+3][c]];
              score += evaluateWindow(window, piece);
          }
      }
      // Diagonals
      for (let r = 0; r <= ROWS - 4; r++) {
          for (let c = 0; c <= COLS - 4; c++) {
              const window = [b[r][c], b[r+1][c+1], b[r+2][c+2], b[r+3][c+3]];
              score += evaluateWindow(window, piece);
          }
      }
      for (let r = 3; r < ROWS; r++) {
         for (let c = 0; c <= COLS - 4; c++) {
              const window = [b[r][c], b[r-1][c+1], b[r-2][c+2], b[r-3][c+3]];
              score += evaluateWindow(window, piece);
          }
      }
      return score;
  };

  const getValidLocations = (b) => {
      const locations = [];
      for(let c=0; c<COLS; c++) {
          if (getLowestEmptyRow(b, c) !== -1) locations.push(c);
      }
      return locations;
  };

  const isTerminalNode = (b) => {
      return checkWin(b, 1) || checkWin(b, 2) || getValidLocations(b).length === 0;
  };

  const minimax = (b, depth, alpha, beta, maximizingPlayer) => {
      const validLocations = getValidLocations(b);
      const isTerminal = isTerminalNode(b);
      
      if (depth === 0 || isTerminal) {
          if (isTerminal) {
              if (checkWin(b, 2)) return { score: 1000000 };
              if (checkWin(b, 1)) return { score: -1000000 };
              return { score: 0 }; // Draw
          } else {
              return { score: scorePosition(b, 2) };
          }
      }

      if (maximizingPlayer) {
          let value = -Infinity;
          let column = validLocations[Math.floor(Math.random() * validLocations.length)];
          for (let col of validLocations) {
              const row = getLowestEmptyRow(b, col);
              const bCopy = b.map(arr => [...arr]);
              bCopy[row][col] = 2; // AI
              const newScore = minimax(bCopy, depth - 1, alpha, beta, false).score;
              if (newScore > value) {
                  value = newScore;
                  column = col;
              }
              alpha = Math.max(alpha, value);
              if (alpha >= beta) break;
          }
          return { column, score: value };
      } else {
          let value = Infinity;
          let column = validLocations[Math.floor(Math.random() * validLocations.length)];
          for (let col of validLocations) {
              const row = getLowestEmptyRow(b, col);
              const bCopy = b.map(arr => [...arr]);
              bCopy[row][col] = 1; // Player
              const newScore = minimax(bCopy, depth - 1, alpha, beta, true).score;
              if (newScore < value) {
                  value = newScore;
                  column = col;
              }
              beta = Math.min(beta, value);
              if (alpha >= beta) break;
          }
          return { column, score: value };
      }
  };

  // AI Move Wrapper
  const getBotMove = useCallback((diff, gameState) => {
      const { board } = gameState;
      const depth = diff === "Easy" ? 1 : (diff === "Medium" ? 3 : 5);
      
      // Ensure we don't freeze UI
      // In JS single thread, deep minimax might hang.
      // 5 is probably strictly max for full board.
      const { column } = minimax(board, depth, -Infinity, Infinity, true);
      
      // Return format expected by useGameBot/handler
      return { col: column };

  }, []);


  const handleMove = useCallback((col) => {
      // Return reducer function
      return (currentBoard, currentPlayer) => {
          const row = getLowestEmptyRow(currentBoard, col);
          if (row === -1) return null; // Invalid

          const newBoard = currentBoard.map(arr => [...arr]);
          newBoard[row][col] = currentPlayer;

          const winPath = checkWin(newBoard, currentPlayer);
          
          if (winPath) {
              return {
                  newBoard,
                  gameOver: true,
                  winner: currentPlayer,
                  winningCells: winPath,
                  nextTurn: currentPlayer === 1 ? 2 : 1
              };
          }

          // Check Draw
          if (getValidLocations(newBoard).length === 0) {
               return {
                  newBoard,
                  gameOver: true,
                  winner: "Draw",
                  winningCells: [],
                  nextTurn: currentPlayer === 1 ? 2 : 1
              };
          }

          return {
              newBoard,
              gameOver: false,
              winner: null,
              winningCells: [],
              nextTurn: currentPlayer === 1 ? 2 : 1
          };
      };
  }, []);

  const executeMove = (col) => {
      if (gameOver || !gameStarted) return;
      
      const logic = handleMove(col);
      const result = logic(board, currentPlayer);
      
      if (!result) return; // Column full

      setBoard(result.newBoard);
      
      if (result.gameOver) {
          setGameOver(true);
          setWinner(result.winner);
          if (result.winningCells) setWinningCells(result.winningCells);
      } else {
          setCurrentPlayer(result.nextTurn);
      }
  };

  const { isBotThinking } = useGameBot({
    gameMode,
    makeMove: ({col}) => executeMove(col),
    playerTurn: currentPlayer,
    gameStarted,
    gameOver,
    botStrategy: getBotMove,
    gameState: { board, currentPlayer },
    difficulty
  });


  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-xl p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Connect 4</h1>
        <p className="text-center text-gray-600 mb-8">
             Drop your pieces to connect four in a row!
        </p>

        {!gameStarted ? (
           <div className="text-center animate-fade-in">
             <GameOptions
               gameMode={gameMode}
               setGameMode={setGameMode}
               difficulty={difficulty}
               setDifficulty={setDifficulty}
               onStart={startGame}
             />
             <PlayerNameInput
                 player1Name={player1Name}
                 setPlayer1Name={setPlayer1Name}
                 player2Name={player2Name}
                 setPlayer2Name={setPlayer2Name}
                 gameMode={gameMode}
             />
             <div className="mt-8 text-gray-600 max-w-2xl mx-auto text-left">
               <h3 className="font-semibold mb-4 text-lg">Rules:</h3>
               <ul className="list-disc pl-5 space-y-2 bg-gray-50 p-4 rounded-lg">
                   <li><strong>Goal:</strong> Be the first to form a horizontal, vertical, or diagonal line of four chips.</li>
                   <li><strong>Play:</strong> Click a column to drop a chip. It falls to the lowest empty spot.</li>
                   <li><strong>Strategy:</strong> Block your opponent's lines while building your own!</li>
               </ul>
             </div>
           </div>
        ) : (
           <div className="animate-fade-in">
               
               {/* Status Header */}
               {/* Status Header */}
               <div className="flex flex-col sm:flex-row justify-between items-center mb-6 px-2 gap-4 sm:gap-0 sm:px-12">
                    <div className={`
                        w-full sm:w-auto text-center px-4 py-2 sm:px-6 sm:py-3 rounded-lg border-2 transition-all duration-300
                        ${currentPlayer === 1 ? 'border-yellow-500 bg-yellow-50 shadow-md sm:scale-105' : 'border-transparent opacity-75'}
                    `}>
                        <div className="text-sm sm:text-lg font-bold text-yellow-700 flex items-center justify-center gap-2">
                             <User className="w-4 h-4 sm:w-5 sm:h-5" /> {getPlayerName(1)} <span className="hidden sm:inline">(Yellow)</span>
                        </div>
                    </div>
                    
                    <div className="text-center py-2 sm:py-0">
                         {gameOver ? (
                             <div className="animate-bounce-in">
                                 <Trophy className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-1 text-purple-600" />
                                 <div className="text-lg sm:text-2xl font-bold text-purple-700">
                                     {winner === "Draw" ? "It's a Draw!" : `${getPlayerName(winner)} Wins!`}
                                 </div>
                             </div>
                         ) : (
                            <div className="text-xl font-bold text-gray-400">VS</div>
                         )}
                         {isBotThinking && !gameOver && <div className="text-purple-500 animate-pulse text-xs sm:text-sm mt-1">Thinking...</div>}
                    </div>

                    <div className={`
                        w-full sm:w-auto text-center px-4 py-2 sm:px-6 sm:py-3 rounded-lg border-2 transition-all duration-300
                        ${currentPlayer === 2 ? 'border-red-500 bg-red-50 shadow-md sm:scale-105' : 'border-transparent opacity-75'}
                    `}>
                        <div className="text-sm sm:text-lg font-bold text-red-700 flex items-center justify-center gap-2">
                             {gameMode === 'PvC' ? <Bot className="w-4 h-4 sm:w-5 sm:h-5" /> : <User className="w-4 h-4 sm:w-5 sm:h-5" />}
                             {getPlayerName(2)} <span className="hidden sm:inline">(Red)</span>
                        </div>
                    </div>
               </div>

               {/* Game Board */}
               <div className="flex justify-center mb-8">
                   <div className="bg-blue-500 p-4 rounded-xl shadow-2xl border-b-8 border-blue-700 relative z-0">
                       
                       {/* Column Buttons / Hover Indicators */}
                       <div className="absolute top-0 left-0 right-0 -mt-8 h-8 flex px-4">
                           {Array.from({ length: COLS }).map((_, c) => (
                               <div 
                                    key={`indicator-${c}`} 
                                    className="flex-1 flex justify-center items-end pb-1 group cursor-pointer"
                                    onClick={() => !gameOver && !isBotThinking && executeMove(c)}
                               >
                                   {!gameOver && !isBotThinking && currentPlayer === 1 && (
                                       <div className="w-6 h-6 rounded-full bg-yellow-400 opacity-0 group-hover:opacity-50 transition-opacity animate-bounce" />
                                   )}
                                   {!gameOver && !isBotThinking && currentPlayer === 2 && (
                                       <div className="w-6 h-6 rounded-full bg-red-500 opacity-0 group-hover:opacity-50 transition-opacity animate-bounce" />
                                   )}
                               </div>
                           ))}
                       </div>

                       {/* Slots */}
                       <div className="grid grid-rows-6 gap-1 md:gap-3">
                           {board.map((row, r) => (
                               <div key={r} className="grid grid-cols-7 gap-1 md:gap-3 justify-items-center">
                                   {row.map((cell, c) => {
                                       const isWinning = winningCells.some(wc => wc.r === r && wc.c === c);
                                       return (
                                           <div 
                                                key={`${r}-${c}`}
                                                className="w-9 h-9 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-blue-600 rounded-full flex items-center justify-center overflow-hidden shadow-inner relative cursor-pointer"
                                                onClick={() => !gameOver && !isBotThinking && executeMove(c)}
                                           >
                                               {/* Hole Background (Darker blue) */}
                                               <div className="absolute inset-0 bg-blue-700 rounded-full opacity-30 pointer-events-none" />
                                               
                                               {/* Piece */}
                                               {cell !== 0 && (
                                                   <div 
                                                      className={`
                                                        w-7 h-7 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full shadow-md z-10 transition-transform duration-500
                                                        ${cell === 1 ? 'bg-yellow-400' : 'bg-red-500'}
                                                        ${isWinning ? 'ring-4 ring-white animate-pulse' : ''}
                                                        animate-drop-in
                                                      `}
                                                   />
                                               )}
                                           </div>
                                       );
                                   })}
                               </div>
                           ))}
                       </div>
                   </div>
               </div>

                <div className="flex justify-center">
                    <button 
                        onClick={() => setGameStarted(false)} 
                        className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 font-bold rounded-full hover:bg-gray-300 transition-colors shadow-sm"
                    >
                        <RefreshCw className="w-5 h-5" />
                        New Game
                    </button>
                </div>

           </div>
        )}
      </div>
    </div>
  );
};

export default ConnectFourGame;
